(() => {
  'use strict';

  function normalizeItem(item) {
    const source = item && typeof item === 'object' ? item : {};
    const groupBuyId = typeof source.groupBuyId === 'string' ? source.groupBuyId.trim() : '';
    const sameProductOnly = source.sameProductOnly === true;
    const legacyRestricted = source.legacyRestricted === true;

    return {
      productId: source.productId,
      groupBuyId,
      sameProductOnly,
      legacyRestricted,
      shippingDateOnProductPage: source.shippingDateOnProductPage === true,
      effectiveSameProductOnly: sameProductOnly || (legacyRestricted && groupBuyId === '')
    };
  }

  function evaluateCart(items) {
    const normalizedItems = Array.isArray(items) ? items.map(normalizeItem) : [];

    if (normalizedItems.length === 0) {
      return { valid: false, reason: 'empty' };
    }

    const sameProductItem = normalizedItems.find((item) => item.effectiveSameProductOnly);

    if (sameProductItem) {
      const valid = normalizedItems.every((item) => item.productId === sameProductItem.productId);
      return { valid, reason: valid ? null : 'same_product_only' };
    }

    const groupItem = normalizedItems.find((item) => item.groupBuyId !== '');

    if (groupItem) {
      const valid = normalizedItems.every((item) => item.groupBuyId !== '' && item.groupBuyId === groupItem.groupBuyId);
      return { valid, reason: valid ? null : 'group_buy_mismatch' };
    }

    return { valid: true, reason: null };
  }

  window.YushakoboCart = Object.freeze({ evaluateCart });

  let activeCartCleanup = null;
  let cartRefreshPending = false;

  function lockCartForRefresh() {
    const cartRoot = document.querySelector('[data-yushakobo-cart]');

    if (!cartRoot) {
      return;
    }

    const checkoutGate = cartRoot.querySelector('[data-yushakobo-cart-checkout-gate]');
    const checkoutButton = cartRoot.querySelector('[data-yushakobo-cart-checkout]');
    const checkoutProxy = cartRoot.querySelector('[data-yushakobo-cart-checkout-proxy]');

    if (!checkoutGate || !checkoutButton || !checkoutProxy) {
      return;
    }

    cartRefreshPending = true;

    checkoutButton.setAttribute('data-yushakobo-cart-checkout-disabled-reason', 'refreshing');
    checkoutGate.disabled = true;
    checkoutProxy.hidden = false;
  }

  function initializeCart() {
    if (activeCartCleanup) {
      activeCartCleanup();
      activeCartCleanup = null;
    }

    const cartRoot = document.querySelector('[data-yushakobo-cart]');

    if (!cartRoot) {
      return;
    }

    const payload = document.getElementById('YushakoboCartRules');

    if (!payload) {
      return;
    }

    let items;

    try {
      const data = JSON.parse(payload.textContent);
      items = Array.isArray(data.items) ? data.items : [];
    } catch (_error) {
      return;
    }

    cartRefreshPending = false;

    const result = evaluateCart(items);
    const alerts = cartRoot.querySelectorAll('[data-yushakobo-cart-alert]');

    alerts.forEach((alert) => {
      alert.hidden = true;
    });

    if (!result.valid && result.reason !== 'empty') {
      const activeAlert = cartRoot.querySelector(`[data-yushakobo-cart-alert="${result.reason}"]`);

      if (activeAlert) {
        activeAlert.hidden = false;
      }
    }

    function loadShippingDate(items) {
      const shippingRoot = cartRoot.querySelector('[data-yushakobo-cart-shipping]');

      if (!shippingRoot) {
        return () => {};
      }

      const shippingLoading = shippingRoot.querySelector('[data-yushakobo-cart-shipping-loading]');
      const shippingResult = shippingRoot.querySelector('[data-yushakobo-cart-shipping-result]');
      const shippingProductPage = shippingRoot.querySelector('[data-yushakobo-cart-shipping-product-page]');
      const shippingFailure = shippingRoot.querySelector('[data-yushakobo-cart-shipping-failure]');

      if (!shippingLoading || !shippingResult || !shippingProductPage || !shippingFailure) {
        return () => {};
      }

      shippingLoading.hidden = false;
      shippingResult.hidden = true;
      shippingProductPage.hidden = true;
      shippingFailure.hidden = true;

      const showShippingDateOnProductPage = items.some((item) => item.shippingDateOnProductPage === true);

      if (showShippingDateOnProductPage) {
        shippingLoading.hidden = true;
        shippingProductPage.hidden = false;
        return () => {};
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      let active = true;

      (async () => {
        try {
          const response = await fetch('https://us-central1-tule-45ddf.cloudfunctions.net/shippingDateApi', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ data: {} }),
            signal: controller.signal
          });

          if (!response.ok) {
            throw new Error('Shipping date request failed');
          }

          const json = await response.json();
          const hasShippingDate = json && typeof json.result === 'string' && json.result.trim() !== '';

          if (!hasShippingDate) {
            throw new Error('Shipping date response was empty');
          }

          if (active) {
            shippingResult.textContent = json.result;
            shippingResult.hidden = false;
          }
        } catch (_error) {
          if (active) {
            shippingFailure.hidden = false;
          }
        } finally {
          clearTimeout(timeoutId);

          if (active) {
            shippingLoading.hidden = true;
          }
        }
      })();

      return () => {
        active = false;
        clearTimeout(timeoutId);
        controller.abort();
        shippingLoading.hidden = true;
      };
    }

    const shippingCleanup = loadShippingDate(items);
    const cartForm = cartRoot.querySelector('[data-yushakobo-cart-form]');
    const termsInput = cartRoot.querySelector('[data-yushakobo-cart-terms]');
    const checkoutGate = cartRoot.querySelector('[data-yushakobo-cart-checkout-gate]');
    const checkoutButton = cartRoot.querySelector('[data-yushakobo-cart-checkout]');
    const checkoutProxy = cartRoot.querySelector('[data-yushakobo-cart-checkout-proxy]');

    if (!cartForm || !termsInput || !checkoutGate || !checkoutButton || !checkoutProxy) {
      activeCartCleanup = () => {
        shippingCleanup();
      };
      return;
    }

    const cartRuleValid = result.valid;
    const termsAlert = cartRoot.querySelector('[data-yushakobo-cart-alert="terms"]');

    function updateCheckoutState() {
      const termsAccepted = termsInput.checked;
      const canCheckout = !cartRefreshPending && termsAccepted && cartRuleValid;

      if (canCheckout) {
        checkoutButton.removeAttribute('data-yushakobo-cart-checkout-disabled-reason');
        checkoutGate.disabled = false;
        checkoutProxy.hidden = true;

        if (termsAlert) {
          termsAlert.hidden = true;
        }

        return true;
      }

      const disabledReasons = [];

      if (cartRefreshPending) {
        disabledReasons.push('refreshing');
      }

      if (!termsAccepted) {
        disabledReasons.push('terms');
      }

      if (!cartRuleValid) {
        disabledReasons.push(result.reason || 'cart_rule');
      }

      checkoutButton.setAttribute('data-yushakobo-cart-checkout-disabled-reason', disabledReasons.join(' '));
      checkoutGate.disabled = true;
      checkoutProxy.hidden = false;
      return false;
    }

    function validateCheckout(event) {
      if (updateCheckoutState()) {
        return;
      }

      event.preventDefault();

      if (cartRefreshPending) {
        checkoutProxy.focus();
        return;
      }

      const invalidReason = cartRuleValid ? 'terms' : result.reason;
      const activeAlert = cartRoot.querySelector(`[data-yushakobo-cart-alert="${invalidReason}"]`);

      if (activeAlert) {
        activeAlert.hidden = false;
        activeAlert.focus();
      }
    }

    function handleTermsChange() {
      if (termsAlert && termsInput.checked) {
        termsAlert.hidden = true;
      }

      updateCheckoutState();
    }

    termsInput.addEventListener('change', handleTermsChange);
    checkoutButton.addEventListener('click', validateCheckout);
    checkoutProxy.addEventListener('click', validateCheckout);
    cartForm.addEventListener('submit', validateCheckout);

    updateCheckoutState();

    activeCartCleanup = () => {
      shippingCleanup();
      termsInput.removeEventListener('change', handleTermsChange);
      checkoutButton.removeEventListener('click', validateCheckout);
      checkoutProxy.removeEventListener('click', validateCheckout);
      cartForm.removeEventListener('submit', validateCheckout);
    };
  }

  initializeCart();
  document.addEventListener('cart:rerendered', initializeCart);
  document.addEventListener('cart:refresh', lockCartForRefresh);
  document.addEventListener('product:added', lockCartForRefresh);
})();
